import React from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TodayIcon from "@mui/icons-material/Today";
import CalendarEventCard from "./CalendarEventCard";
import CalendarMonthView from "./CalendarMonthView";
import SelectedDayPanel from "./SelectedDayPanel";
import { CalendarServiceInstance, MobileCalendarView, addDays, formatLongDate, formatWeekRange, instancesForDay, sameDay, startOfMonthGrid, startOfWeek } from "./calendarUtils";

interface Props {
  view: MobileCalendarView;
  selectedDate: Date;
  instances: CalendarServiceInstance[];
  onViewChange: (view: MobileCalendarView) => void;
  onSelectDate: (date: Date) => void;
  onNavigate: (direction: "prev" | "next") => void;
  onToday: () => void;
  onOpen: (instance: CalendarServiceInstance) => void;
}

export default function MobileCalendar({ view, selectedDate, instances, onViewChange, onSelectDate, onNavigate, onToday, onOpen }: Props) {
  const weekStart = startOfWeek(selectedDate);
  const days = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  const dayInstances = instancesForDay(instances, selectedDate);
  const title = view === "month" ? selectedDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : formatWeekRange(selectedDate, false);

  return (
    <div className="calendar-mobile">
      <nav className="calendar-mobile-tabs" aria-label="Calendar view">
        {([
          ["agenda", <EventNoteIcon />, "Agenda"],
          ["day", <TodayIcon />, "Day"],
          ["month", <CalendarMonthIcon />, "Month"],
        ] as [MobileCalendarView, React.ReactNode, string][]).map(([option, icon, label]) => <button key={option} type="button" className={view === option ? "is-active" : ""} aria-pressed={view === option} onClick={() => onViewChange(option)}>{icon}<span>{label}</span></button>)}
      </nav>

      <div className="calendar-mobile-navigation">
        <button type="button" className="calendar-today-button" onClick={onToday}>Today</button>
        <button type="button" className="calendar-icon-button" onClick={() => onNavigate("prev")} aria-label={`Previous ${view === "month" ? "month" : "week"}`}><ChevronLeftIcon /></button>
        <strong>{title}</strong>
        <button type="button" className="calendar-icon-button" onClick={() => onNavigate("next")} aria-label={`Next ${view === "month" ? "month" : "week"}`}><ChevronRightIcon /></button>
      </div>

      {view !== "month" && <div className="calendar-mobile-days">
        {days.map((day) => <button key={day.toISOString()} type="button" className={sameDay(day, selectedDate) ? "is-selected" : ""} onClick={() => onSelectDate(day)} aria-pressed={sameDay(day, selectedDate)} aria-current={sameDay(day, new Date()) ? "date" : undefined}><span>{day.toLocaleDateString("en-GB", { weekday: "short" })}</span><strong>{day.getDate()}</strong></button>)}
      </div>}

      {view === "agenda" && <SelectedDayPanel selectedDate={selectedDate} instances={instances} onOpen={onOpen} className="calendar-mobile-agenda" />}

      {view === "day" && <section className="calendar-mobile-day-view">
        <header><h2>{formatLongDate(selectedDate)}</h2><p>{dayInstances.length} service{dayInstances.length === 1 ? "" : "s"} scheduled</p></header>
        <div className="calendar-mobile-timeline">
          {dayInstances.map((instance) => <div className="calendar-mobile-timeline-row" key={instance.serviceInstanceId}><time>{new Date(instance.startDateTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</time><CalendarEventCard instance={instance} onOpen={onOpen} compact /></div>)}
          {dayInstances.length === 0 && <div className="calendar-day-empty">No services scheduled for this day.</div>}
        </div>
      </section>}

      {view === "month" && <><CalendarMonthView mobile gridStart={startOfMonthGrid(selectedDate)} selectedDate={selectedDate} instances={instances} onSelectDate={onSelectDate} onOpen={onOpen} /><SelectedDayPanel selectedDate={selectedDate} instances={instances} onOpen={onOpen} className="calendar-mobile-month-agenda" /></>}

    </div>
  );
}
